import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import BlueprintCanvas from "@/components/BlueprintCanvas";
import CostBreakdown from "@/components/CostBreakdown";
import ThreeDModel from "@/components/ThreeDModel";
import { api, mediaUrl, money } from "@/lib/api";
import { QuoteComparison } from "@/components/QuoteComparison";
import { PushToggle } from "@/components/PushToggle";
import { useBilling } from "@/context/BillingContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Loader2, RefreshCw, Save, ArrowLeft, Sparkles, Box, Share2, Check, FileDown, Crown, Mail, Send } from "lucide-react";
import { toast } from "sonner";

const Spec = ({ label, value }) => (
  <div className="border-r border-border px-4 py-3 last:border-r-0">
    <div className="font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="mt-0.5 font-mono2 text-sm font-semibold capitalize text-foreground">{value}</div>
  </div>
);

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { billing } = useBilling();
  const [project, setProject] = useState(null);
  const [blueprint, setBlueprint] = useState({ rooms: [], walls: [] });
  const [reest, setReest] = useState(false);
  const [savingBp, setSavingBp] = useState(false);
  const [copied, setCopied] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [emailing, setEmailing] = useState(false);

  const canPdf = billing?.can_export_pdf;

  const loadQuotes = () => api.get(`/projects/${id}/quotes`).then(({ data }) => setQuotes(data)).catch(() => {});

  const sendToContractor = async () => {
    if (!emailTo.includes("@")) return toast.error("Enter a valid contractor email.");
    setEmailing(true);
    try {
      const { data } = await api.post(`/projects/${id}/email-scope`, { to_email: emailTo, message: emailMsg });
      setEmailOpen(false);
      setEmailTo(""); setEmailMsg("");
      loadQuotes();
      toast.success(data.sent ? "Scope PDF sent to the contractor" : "Tracked — share the reply link (email is not enabled yet)");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send");
      if (e.response?.status === 402) navigate("/billing");
    } finally {
      setEmailing(false);
    }
  };

  const copyReplyLink = (url) => {
    navigator.clipboard?.writeText(url);
    toast.success("Reply link copied");
  };

  const downloadPdf = () => {
    if (!canPdf) {
      toast.error("PDF scope export is a VIP / Business feature.");
      navigate("/billing");
      return;
    }
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${id}/scope-pdf`, "_blank");
  };

  const setShareCurrency = async (cur) => {
    if (cur === (project.display_currency || project.estimate?.summary?.currency)) return;
    setProject((p) => ({ ...p, display_currency: cur }));
    try {
      await api.put(`/projects/${id}`, { display_currency: cur });
      toast.success("Shared link will now default to " + cur);
    } catch {
      toast.error("Could not save currency");
    }
  };

  const share = async () => {
    const url = `${window.location.origin}/share/${project.share_token}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      /* clipboard may be blocked */
    }
    setCopied(true);
    toast.success("Public link copied — anyone can open and spin the 3D model");
    setTimeout(() => setCopied(false), 2500);
  };

  useEffect(() => {
    api.get(`/projects/${id}`).then(({ data }) => {
      setProject(data);
      setBlueprint(data.blueprint || { rooms: [], walls: [] });
    }).catch(() => {
      toast.error("Project not found");
      navigate("/dashboard");
    });
    api.get("/fx").then(({ data }) => setCurrencies(data.currencies || [])).catch(() => {});
    loadQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  const reestimate = async () => {
    setReest(true);
    try {
      const { data } = await api.post(`/projects/${id}/estimate`);
      setProject((p) => ({ ...p, estimate: data }));
      toast.success("Estimate refreshed");
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error(String(e.response.data.detail));
        navigate("/billing");
      } else {
        toast.error("Could not refresh estimate");
      }
    } finally {
      setReest(false);
    }
  };

  const saveBlueprint = async () => {
    setSavingBp(true);
    try {
      await api.put(`/projects/${id}`, { blueprint });
      toast.success("Blueprint saved");
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error(String(e.response.data.detail));
        navigate("/billing");
      } else {
        toast.error("Could not save blueprint");
      }
    } finally {
      setSavingBp(false);
    }
  };

  if (!project)
    return (
      <div className="min-h-screen bp-grid">
        <Navbar />
        <div className="flex justify-center py-32"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      </div>
    );

  const s = project.spec || {};
  const aiTag = project.estimate?.generated_with === "ai";

  return (
    <div className="min-h-screen bp-grid">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-8">
        <Link to="/dashboard" className="mb-4 inline-flex items-center gap-1.5 font-mono2 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground" data-testid="back-to-dashboard">
          <ArrowLeft className="h-3.5 w-3.5" /> Projects
        </Link>

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tighter sm:text-4xl">{project.name}</h1>
            <div className="mt-1 flex items-center gap-2">
              <span className="font-mono2 text-xs uppercase tracking-widest text-primary">
                {s.building_type?.replace(/_/g, " ")}
              </span>
              <span className="border border-border px-1.5 py-0.5 font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground">
                {aiTag ? "AI estimate" : "Estimated"}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={share} variant="outline" data-testid="share-project-btn"
              className="rounded-sm border-border font-mono2 uppercase tracking-widest">
              {copied ? <Check className="mr-2 h-4 w-4 text-primary" /> : <Share2 className="mr-2 h-4 w-4" />} Share
            </Button>
            <Button onClick={downloadPdf} variant="outline" data-testid="download-pdf-btn"
              className="rounded-sm border-border font-mono2 uppercase tracking-widest">
              {canPdf ? <FileDown className="mr-2 h-4 w-4" /> : <Crown className="mr-2 h-4 w-4 text-primary" />} PDF
            </Button>
            <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="email-contractor-btn"
                  onClick={(e) => { if (!canPdf) { e.preventDefault(); toast.error("Emailing the scope is a VIP / Business feature."); navigate("/billing"); } }}
                  className="rounded-sm border-border font-mono2 uppercase tracking-widest">
                  {canPdf ? <Mail className="mr-2 h-4 w-4" /> : <Crown className="mr-2 h-4 w-4 text-primary" />} Email Contractor
                </Button>
              </DialogTrigger>
              <DialogContent className="border-border">
                <DialogHeader><DialogTitle className="font-heading">Email the scope PDF</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Contractor email</label>
                    <Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} data-testid="email-to-input"
                      placeholder="builder@example.com" className="mt-1.5 rounded-sm border-border bg-transparent" />
                  </div>
                  <div>
                    <label className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Message (optional)</label>
                    <Textarea value={emailMsg} onChange={(e) => setEmailMsg(e.target.value)} data-testid="email-msg-input" rows={4}
                      placeholder="Hi — please quote this scope of work…" className="mt-1.5 rounded-sm border-border bg-transparent" />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={sendToContractor} disabled={emailing} data-testid="email-send-btn"
                    className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
                    {emailing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Send PDF
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Button onClick={reestimate} disabled={reest} data-testid="reestimate-btn"
              className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
              {reest ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Recalculating…</> : <><RefreshCw className="mr-2 h-4 w-4" /> Re-estimate</>}
            </Button>
          </div>
        </div>

        {/* Share default currency */}
        {currencies.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-3 border border-border bg-secondary/10 px-4 py-3" data-testid="share-currency-default">
            <Share2 className="h-4 w-4 text-primary" />
            <span className="font-mono2 text-[11px] uppercase tracking-widest text-muted-foreground">Share link opens in</span>
            <Select value={project.display_currency || project.estimate?.summary?.currency || "USD"} onValueChange={setShareCurrency}>
              <SelectTrigger data-testid="share-currency-default-select" className="h-8 w-40 rounded-sm border-border bg-transparent font-mono2 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="font-mono2 text-[11px] uppercase tracking-widest text-muted-foreground">for partners</span>
          </div>
        )}

        {/* Quote tracking & comparison */}
        {quotes.length > 0 && (
          <>
            <div className="mt-4 flex items-center justify-end gap-2">
              <span className="font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground">Get notified on new quotes</span>
              <PushToggle />
            </div>
            <QuoteComparison quotes={quotes} onCopyLink={copyReplyLink} />
          </>
        )}

        {/* spec strip */}
        <div className="mt-6 grid grid-cols-2 border border-border sm:grid-cols-3 md:grid-cols-6">
          <Spec label="Sq Ft" value={s.square_footage} />
          <Spec label="Floors" value={s.floors} />
          <Spec label="Beds" value={s.bedrooms} />
          <Spec label="Baths" value={s.bathrooms} />
          <Spec label="Finish" value={s.finish_level} />
          <Spec label="Region" value={s.region} />
        </div>

        {/* estimate */}
        <div className="mt-10">
          <div className="mb-4 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <h2 className="font-heading text-xl font-semibold">Full scope & cost estimate</h2>
          </div>
          <CostBreakdown estimate={project.estimate} />
        </div>

        {/* 3D model */}
        {project.model3d && (
          <div className="mt-12" data-testid="project-3d-section">
            <div className="mb-4 flex items-center gap-2">
              <Box className="h-4 w-4 text-primary" />
              <h2 className="font-heading text-xl font-semibold">AI-generated 3D model</h2>
            </div>
            <div className="grid gap-6 lg:grid-cols-2">
              <div>
                <p className="mb-3 font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Interactive model — drag to orbit</p>
                <ThreeDModel floorplan={project.model3d.floorplan} />
              </div>
              {project.model3d.render_url && (
                <div>
                  <p className="mb-3 font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Photorealistic render</p>
                  <div className="flex min-h-[300px] items-center justify-center overflow-hidden border border-border bg-[#050a16]">
                    <img src={mediaUrl(project.model3d.render_url)} alt="render" className="h-full w-full object-cover" />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* blueprint */}
        <div className="mt-12">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-xl font-semibold">Blueprint</h2>
            <Button onClick={saveBlueprint} disabled={savingBp} variant="outline" size="sm" data-testid="save-blueprint-btn"
              className="rounded-sm border-border font-mono2 text-xs uppercase tracking-widest">
              {savingBp ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />} Save
            </Button>
          </div>
          <div className="border border-border p-6">
            <BlueprintCanvas value={blueprint} onChange={setBlueprint} />
          </div>
        </div>
      </div>
    </div>
  );
}
